import type { ISerialize } from '@/objects/ISerialize';
import type { TimeExitStatus } from './../enums/TimeExitStatus';
import { formatDateIso } from '@/utilities/dateutils';

export class RaceContestantTime implements ISerialize {
    public AccumulatedTime: number;

    public AccumulatedTimeSmooth: number;

    public Bib: number | null;

    public ClockTime: Date;

    public ContestantID: number;

    public EventID: number;

    public readonly Id: string | null;

    public LapNumber: number;

    public PenaltyString: string | null;

    public RaceContestantID: number;

    public StationRoleID: number;

    public Status: TimeExitStatus;

    public TUID: string;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        AccumulatedTime?: number;
        AccumulatedTimeSmooth?: number;
        Bib?: number | null;
        ClockTime?: Date;
        ContestantID?: number;
        EventID?: number;
        Id?: string | null;
        LapNumber?: number;
        PenaltyString?: string | null;
        RaceContestantID?: number;
        StationRoleID?: number;
        Status?: TimeExitStatus;
        TUID?: string | null;
    }) {
        this.AccumulatedTime = values.AccumulatedTime ?? 0;

        this.AccumulatedTimeSmooth = values.AccumulatedTimeSmooth ?? 0;

        this.Bib = values.Bib ?? null;

        this.ClockTime = values.ClockTime ?? new Date('1970-01-01T00:00:00.000');

        this.ContestantID = values.ContestantID ?? 0;

        this.EventID = values.EventID ?? 0;

        this.Id = values.Id ?? '';

        this.LapNumber = values.LapNumber ?? 0;

        this.PenaltyString = values.PenaltyString ?? '';

        this.RaceContestantID = values.RaceContestantID ?? 0;

        this.StationRoleID = values.StationRoleID ?? 0;

        this.Status = values.Status ?? 'None';

        this.TUID = values.TUID ?? '';
    }

    public serialize(): string {
        return (
            '{' +
            `"AccumulatedTime": ${JSON.stringify(this.AccumulatedTime)}` +
            ',' +
            `"AccumulatedTimeSmooth": ${JSON.stringify(this.AccumulatedTimeSmooth)}` +
            ',' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"ClockTime": ${'"' + formatDateIso(this.ClockTime) + '"'}` +
            ',' +
            `"ContestantID": ${JSON.stringify(this.ContestantID)}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Id": ${JSON.stringify(this.Id)}` +
            ',' +
            `"LapNumber": ${JSON.stringify(this.LapNumber)}` +
            ',' +
            `"PenaltyString": ${JSON.stringify(this.PenaltyString)}` +
            ',' +
            `"RaceContestantID": ${JSON.stringify(this.RaceContestantID)}` +
            ',' +
            `"StationRoleID": ${JSON.stringify(this.StationRoleID)}` +
            ',' +
            `"Status": ${this.Status == 'None' ? 0 : this.Status == 'Deleted' ? 1 : this.Status == 'Time' ? 2 : this.Status == 'Finished' ? 3 : this.Status == 'REL' ? 4 : this.Status == 'DNC' ? 5 : this.Status == 'DNF' ? 6 : this.Status == 'DSQ' ? 7 : this.Status == 'DNS' ? 8 : this.Status == 'DNQ' ? 9 : this.Status == 'LAP' ? 10 : 0}` +
            ',' +
            `"TUID": ${JSON.stringify(this.TUID)}` +
            '}'
        );
    }

    /**
     * Creates an instance of RaceContestantTime from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        AccumulatedTime?: number;
        AccumulatedTimeSmooth?: number;
        Bib?: number | null;
        ClockTime?: Date;
        ContestantID?: number;
        EventID?: number;
        LapNumber?: number;
        PenaltyString?: string | null;
        RaceContestantID?: number;
        StationRoleID?: number;
        Status?: TimeExitStatus;
        TUID?: string | null;
    }): RaceContestantTime {
        return new RaceContestantTime(values);
    }

    public static _FromServerJson(json: any): RaceContestantTime {
        return new RaceContestantTime({
            AccumulatedTime: json.AccumulatedTime,
            AccumulatedTimeSmooth: json.AccumulatedTimeSmooth,
            Bib: json.Bib ?? null,
            ClockTime: json.ClockTime instanceof Date ? json.ClockTime : new Date(json.ClockTime),
            ContestantID: json.ContestantID,
            EventID: json.EventID,
            Id: json.Id ?? '',
            LapNumber: json.LapNumber,
            PenaltyString: json.PenaltyString ?? '',
            RaceContestantID: json.RaceContestantID,
            StationRoleID: json.StationRoleID,
            Status:
                json.Status == 0
                    ? 'None'
                    : json.Status == 1
                      ? 'Deleted'
                      : json.Status == 2
                        ? 'Time'
                        : json.Status == 3
                          ? 'Finished'
                          : json.Status == 4
                            ? 'REL'
                            : json.Status == 5
                              ? 'DNC'
                              : json.Status == 6
                                ? 'DNF'
                                : json.Status == 7
                                  ? 'DSQ'
                                  : json.Status == 8
                                    ? 'DNS'
                                    : json.Status == 9
                                      ? 'DNQ'
                                      : json.Status == 10
                                        ? 'LAP'
                                        : 'None',
            TUID: json.TUID ?? '',
        });
    }

    /**
     * Creates an instance of RaceContestantTime from another instance of RaceContestantTime.
     * @param other The RaceContestantTime you want to clone
     * @returns A cloned RaceContestantTime
     */
    public static FromOther(other: RaceContestantTime): RaceContestantTime {
        return RaceContestantTime._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return RaceContestantTime.FromOther(this);
    }

    static NAME_OF_CLASS = 'RaceContestantTime';
}
